import React, { useMemo, useEffect } from "react";
import ReactModal from "react-modal";
import { motion, AnimatePresence } from "framer-motion";
import { Formik, Form } from "formik";
import { axiosget, axiospost } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FormSkeleton } from "../../components/ui/Skeleton";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import FormSelect from "../../components/ui/FormSelect";
import is_money from "../../helpers/is_money";
import { contextapi } from "../../helpers/contextapi";
import Swal from "sweetalert2";
import { FiDollarSign, FiX, FiCreditCard } from "react-icons/fi";
import { toast } from "react-toastify";

const BtnGenerateRRR = ({ inline = false, quickApplySessionId }) => {
  const [showmodal, setshowmodal] = React.useState(false);
  const [program, setprogram] = React.useState(false);
  const [selectedProgramme, setSelectedProgramme] = React.useState("");
  const [isGenerating, setIsGenerating] = React.useState(false);
  const context = React.useContext(contextapi);
  const queryClient = useQueryClient();
  const [user, setUser] = React.useState(null)

  useEffect(() => {
    // Try to get user from context first, then from localStorage
    let currentUser = context?.values?.user;
    if (!currentUser || !currentUser?.firstname) {
      try {
        const storedUser = localStorage.getItem("isApplicantData");
        if (storedUser) {
          currentUser = JSON.parse(storedUser);
        }
      } catch (e) {
        console.error("Error parsing stored user data:", e);
      }
    }
    setUser(currentUser);
  }, [context?.values?.user]);

  const _show = () => {
    setshowmodal(true);
  };
  const { data } = useQuery({
    queryKey: ["paymentProgramList"],
    queryFn: async () => {
      const req = await axiosget("ms_programme");
      return req?.data;
    },
  });

  // Transform programmes into select options
  const programmeOptions = useMemo(() => {
    if (!data || data?.type !== undefined) return [];
    return data.map((r) => ({
      value: r?._id,
      label: `${r.code} - ${r.name}`,
    }));
  }, [data]);

  // Watch for programme selection changes
  useEffect(() => {
    if (selectedProgramme) {
      setprogram(selectedProgramme);
    }
  }, [selectedProgramme]);

  const {
    data: table,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["paymentCost", program],
    queryFn: async () => {
      const req = await axiosget(`ms_application_fee_items/${program}`);
      return req?.data;
    },
    enabled: !!program,
  });
  let amount = 0;
  if (!error && table?.type === undefined) amount = table?.map((v) => v.amount).reduce((p, a) => p + a, 0);
  console.log(table)
  const _generateRRR = async () => {
    try {
      setIsGenerating(true);

      if (!program) {
        swal("error", "Please select a programme first");
        setIsGenerating(false);
        return;
      }

      // Route RRR generation through the backend — credentials stay server-side
      const saveResponse = await axiospost("ms_application_fee/generate/rrr", {
        programme: program,
      });

      setIsGenerating(false);

      if (saveResponse?.data?.type === "error") {
        swal("error", saveResponse?.data?.message);
        return;
      }

      if (saveResponse?.data?.type !== "success") {
        swal("error", "Failed to generate RRR. Please try again.");
        return;
      }

      const { rrr } = saveResponse.data.data;

      // Refresh payment list and close modal
      queryClient.invalidateQueries({ queryKey: ["generatedRRR"] });
      queryClient.invalidateQueries({ queryKey: ["dashboardDataset"] });
      setshowmodal(false);

      // Show success with RRR
      Swal.fire({
        icon: "success",
        title: "RRR Generated Successfully",
        html: `
          <div class="text-center">
            <p class="text-neutral-600 mb-4">Your Remita Retrieval Reference (RRR) has been generated</p>
            <div class="bg-primary-50 border-2 border-primary-200 rounded-lg p-4 mb-4">
              <p class="text-sm font-medium text-primary-900 mb-2">RRR Code</p>
              <p class="text-2xl font-bold text-primary-600 font-mono">${rrr}</p>
            </div>
            <p class="text-sm text-neutral-500">Please copy this RRR for payment</p>
          </div>
        `,
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-lg",
        },
      });
    } catch (error) {
      setIsGenerating(false);
      swal("error", error?.message || "Failed to generate RRR. Please try again.");
    }
  };
  return (
    <div>
      <ReactModal
        isOpen={showmodal}
        onRequestClose={() => setshowmodal(false)}
        className="ReactModal"
        overlayClassName="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 z-50"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="bg-white rounded-xl shadow-2xl w-[75vw] h-[75vh] overflow-hidden flex flex-col"
          role="document"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 px-6 py-5 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-lg">
                <FiDollarSign className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h5 className="text-xl font-semibold text-white">
                  Generate Remita RRR For Payment
                </h5>
                <p className="text-primary-100 text-sm mt-0.5">
                  Select programme and generate payment reference
                </p>
              </div>
              <button
                onClick={() => setshowmodal(false)}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                aria-label="Close modal"
              >
                <FiX className="h-6 w-6 text-white" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-1">
            <Formik
              initialValues={{
                programme: "",
              }}
              onSubmit={(values) => {
                setprogram(values.programme);
              }}
            >
              {({ values, setFieldValue }) => {
                // Update selected programme when form value changes
                if (values.programme !== selectedProgramme) {
                  setSelectedProgramme(values.programme);
                }

                return (
                  <Form className="space-y-6">
                    {/* Programme Selection */}
                    <FormSelect
                      name="programme"
                      label="Select Programme You Are Applying For"
                      options={programmeOptions}
                      required
                      searchable
                      clearable
                      helperText="Search or scroll to find your programme"
                    />

                    {/* Loading State */}
                    {isLoading && (
                      <div className="mt-4">
                        <FormSkeleton fields={1} />
                      </div>
                    )}

                    {/* Error States */}
                    {error && (
                      <Alert variant="danger">
                        <AlertDescription>
                          Error encountered: {error?.message || error}
                        </AlertDescription>
                      </Alert>
                    )}

                    {table?.type !== undefined && (
                      <Alert variant="info">
                        <AlertDescription>{table?.message}</AlertDescription>
                      </Alert>
                    )}

                    {/* Payment Breakdown Table */}
                    {table && table?.type === undefined && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="space-y-4"
                      >
                        <div className="bg-neutral-50 rounded-lg border border-neutral-200 overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-neutral-100 border-b border-neutral-200">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">SN</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">Title</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-700 uppercase tracking-wider">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-200">
                              {table?.map((r, i) => (
                                <tr key={`payment_items_${i}${r?._id}`} className="hover:bg-neutral-50 transition-colors">
                                  <td className="px-4 py-3 text-sm font-medium text-neutral-900">{i + 1}</td>
                                  <td className="px-4 py-3 text-sm text-neutral-700">{r.name}</td>
                                  <td className="px-4 py-3 text-sm font-semibold text-neutral-900 text-right">{is_money(r.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-primary-50 border-t-2 border-primary-200">
                              <tr>
                                <td colSpan="2" className="px-4 py-3 text-sm font-semibold text-primary-900">Total Amount</td>
                                <td className="px-4 py-3 text-lg font-bold text-primary-900 text-right">{is_money(amount)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        {/* Generate RRR Button */}
                        <div className="pt-4 border-t border-neutral-200">
                          <Button
                            variant="primary"
                            size="lg"
                            fullWidth
                            onClick={_generateRRR}
                            loading={isGenerating}
                            disabled={isGenerating}
                            rightIcon={!isGenerating && <FiCreditCard />}
                          >
                            {isGenerating ? "Generating RRR..." : `Generate RRR Code For ${is_money(amount)}`}
                          </Button>
                        </div>
                      </motion.div>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </div>
        </motion.div>
      </ReactModal>
      {inline ? (
        <div className="flex justify-end mb-3">
          <Button
            variant="primary"
            onClick={_show}
            leftIcon={<FiCreditCard className="h-4 w-4" />}
          >
            Generate Payment
          </Button>
        </div>
      ) : (
        /* FAB for generating RRR */
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          className="fixed right-6 bottom-6 md:right-12 md:bottom-12 z-40"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={_show}
            className="h-14 w-14 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-full shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center group"
            aria-label="Generate RRR"
          >
            <svg className="h-6 w-6 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default BtnGenerateRRR;
